/* AIPathwayPreview.jsx — Path #4 full preview page (USER view).
 *
 * MIRRORS THE ADMIN /explore/ai/role/:id PAGE EXACTLY — same hero, same
 * sticky section nav, same Snapshot/Who/Journey/Readiness/Fit/Skills/
 * AI-in-Prof/AI-Agents/Risk/Growth/Portfolio/Earning/Mistakes blocks,
 * with the same renderers, labels, accents, icons, and copy.
 *
 * The ONLY difference from admin AIRoleDetail.jsx:
 *   • Fetches a single role from /api/ai-pathway/{role_id} (lean payload,
 *     gate-protected) instead of loading the full 125-role JSON into the
 *     browser. This keeps the picker's anti-scrape guarantee intact.
 *   • Back-link points to /ai-pathway (the picker), not /explore/ai.
 *   • No prev/next adjacent nav (user only has 3 picks).
 *   • No QuotaBanner / BuildBadgeCTA / PreviewUnlockOverlay (user is
 *     already past the gate).
 *
 * Every field present on the role record is rendered. Admin code is NOT
 * touched.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { recordCardEvent } from '../lib/clarityCard';
import {
  ArrowLeft, Loader2, Sparkles, Compass, Target, Lightbulb,
  GraduationCap, ChevronRight, Quote, TrendingUp, ShieldAlert, DollarSign,
  CheckCircle2, Activity, AlertCircle, Bot, Building2,
  Zap, Star, Wallet, ListChecks, Rocket, Users, Footprints, Layers, Scale,
  FileSearch,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { LiveOneDayButton } from '../components/stories/OkaRojuPlayer';
import { AgentRealitySection, LearningExtras, OutlookSection, EarningRoutes, Pill } from '../components/ai-pathway/PreviewContent';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'agent-reality',  label: 'AI Reality',                icon: Bot },
  { id: 'snapshot',       label: 'Snapshot',                  icon: Sparkles },
  { id: 'market-evidence', label: 'Market Evidence',          icon: FileSearch },
  { id: 'who',            label: 'Who Is This For?',          icon: Target },
  { id: 'why-exciting',   label: 'Why Exciting',              icon: Zap },
  { id: 'journey',        label: 'Career Journey',            icon: Compass },
  { id: 'readiness',      label: 'Readiness Score',           icon: Activity },
  { id: 'reality',        label: 'Reality Check',             icon: Star },
  { id: 'earning-potential', label: 'Earning Potential',      icon: Wallet },
  { id: 'fit',            label: 'Career Fit',                icon: CheckCircle2 },
  { id: 'skills',         label: 'Skills',                    icon: GraduationCap },
  { id: 'learning-seq',   label: 'Learning Sequence',         icon: ListChecks },
  { id: 'ai-in-prof',     label: 'AI in this Profession',     icon: Sparkles },
  { id: 'ai-agents',      label: 'AI Agents Integrated',      icon: Bot },
  { id: 'role-2030',      label: 'This Role in 2030',         icon: Rocket },
  { id: 'outlook-2035',   label: 'Outlook 2035',              icon: Rocket },
  { id: 'ai-team',        label: 'Your AI Team',              icon: Users },
  { id: 'realistic-path', label: 'A Realistic Path',          icon: Footprints },
  { id: 'risks',          label: 'Risk Analysis',             icon: ShieldAlert },
  { id: 'growth',        label: 'Growth Potential',          icon: TrendingUp },
  { id: 'similar',       label: 'Similar Careers',           icon: Layers },
  { id: 'employers',     label: 'Example Employers',         icon: Building2 },
  { id: 'portfolio',     label: 'Portfolio Ideas',           icon: Lightbulb },
  { id: 'earning',       label: 'Earning Paths',             icon: DollarSign },
  { id: 'choose',        label: 'Choose / Think Twice',      icon: Scale },
  { id: 'mistakes',      label: 'Common Mistakes',           icon: AlertCircle },
];

const ACCENTS = {
  teal:    { card: 'border-teal-200 bg-teal-50/40',       icon: 'from-teal-400 to-emerald-500' },
  amber:   { card: 'border-amber-200 bg-amber-50/40',     icon: 'from-amber-400 to-orange-500' },
  rose:    { card: 'border-rose-200 bg-rose-50/40',       icon: 'from-rose-400 to-pink-500' },
  indigo:  { card: 'border-indigo-200 bg-indigo-50/40',   icon: 'from-indigo-400 to-violet-500' },
  emerald: { card: 'border-emerald-200 bg-emerald-50/40', icon: 'from-emerald-400 to-teal-500' },
  purple:  { card: 'border-purple-200 bg-purple-50/40',   icon: 'from-purple-400 to-fuchsia-500' },
  cyan:    { card: 'border-cyan-200 bg-cyan-50/40',       icon: 'from-cyan-400 to-sky-500' },
  blue:    { card: 'border-blue-200 bg-blue-50/40',       icon: 'from-blue-400 to-indigo-500' },
};

const Section = ({ id, icon: Icon, title, accent = 'teal', children, testId }) => {
  const pal = ACCENTS[accent] || ACCENTS.teal;
  return (
    <section
      id={id}
      data-testid={testId}
      className={`bg-white border-2 ${pal.card} rounded-3xl p-5 md:p-7 shadow-sm scroll-mt-52`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${pal.icon} text-white flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon size={18} />
        </span>
        <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{title}</h2>
      </div>
      <div className="text-gray-700 text-base leading-relaxed">{children}</div>
    </section>
  );
};

const Bullets = ({ items, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-100 text-teal-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    rose: 'bg-rose-100 text-rose-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <ul className="space-y-2">
      {(items || []).map((x, i) => (
        <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed">
          <span className={`w-5 h-5 rounded-full ${tones[tone]} flex items-center justify-center mt-0.5 shrink-0`}>
            <ChevronRight size={12} />
          </span>
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
};

const ChipList = ({ items, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    purple: 'bg-purple-50 text-purple-800 border-purple-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  };
  return (
    <div className="flex flex-wrap gap-2">
      {(items || []).map((x, i) => (
        <span key={i} className={`text-sm px-3 py-1.5 rounded-full border font-medium ${tones[tone]}`}>{x}</span>
      ))}
    </div>
  );
};

const InfoCard = ({ label, children, tone = 'teal' }) => {
  const tones = {
    teal:    'bg-teal-50 border-teal-200',
    sky:     'bg-sky-50 border-sky-200',
    mint:    'bg-emerald-50 border-emerald-200',
    cream:   'bg-amber-50 border-amber-200',
    violet:  'bg-violet-50 border-violet-200',
    rose:    'bg-rose-50 border-rose-200',
  };
  const labelTones = {
    teal: 'text-teal-800', sky: 'text-sky-800', mint: 'text-emerald-800',
    cream: 'text-amber-800', violet: 'text-violet-800', rose: 'text-rose-800',
  };
  return (
    <div className={`rounded-2xl border ${tones[tone] || tones.teal} p-4 md:p-5`}>
      {label && <p className={`text-[11px] uppercase font-bold tracking-[0.18em] mb-1.5 ${labelTones[tone]}`}>{label}</p>}
      <div className="text-[15px] text-gray-800 leading-relaxed">{children}</div>
    </div>
  );
};

const TIER_PCT = { Foundation: 25, Developing: 50, Strong: 75, Advanced: 100 };
const asPct = (v) => (typeof v === 'number' ? Math.max(0, Math.min(100, v || 0)) : (TIER_PCT[v] || 0));

const ScoreRing = ({ value }) => {
  const isTier = typeof value === 'string';
  const pct = asPct(value);
  const r = 42;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} stroke="#E5E7EB" strokeWidth="10" fill="none" />
        <circle
          cx="50" cy="50" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
          stroke="url(#aiprev-grad)"
          strokeDasharray={c}
          strokeDashoffset={off}
        />
        <defs>
          <linearGradient id="aiprev-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#14B8A6" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {isTier ? (
          <>
            <span className="text-lg font-extrabold text-gray-900 text-center leading-tight px-3">{value}</span>
            <span className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mt-0.5">readiness</span>
          </>
        ) : (
          <>
            <span className="text-3xl font-extrabold text-gray-900">{pct}</span>
            <span className="text-[10px] uppercase font-bold tracking-widest text-gray-500">/ 100</span>
          </>
        )}
      </div>
    </div>
  );
};

const ReadinessOverall = ({ value }) => {
  if (value == null) return null;
  if (typeof value === 'number' || TIER_PCT[value]) return <ScoreRing value={value} />;
  return <Pill tone="gray" testId="ai-readiness-unassessed">Readiness: {String(value)}</Pill>;
};

const FactorBar = ({ label, value }) => {
  const pct = asPct(value);
  const assessed = typeof value === 'number' || !!TIER_PCT[value];
  if (!assessed) {
    return <div className="flex items-baseline justify-between"><span className="text-sm font-semibold text-gray-800">{label}</span><span className="text-sm font-bold text-gray-500">{String(value)}</span></div>;
  }
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        <span className="text-sm font-bold text-gray-900">{typeof value === 'number' ? pct : value}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-teal-400 to-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const humanize = (k) =>
  k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\s+/, '').replace(/^\w/, (c) => c.toUpperCase());

const RISK_TONES = {
  High:   'bg-rose-100 text-rose-800 border-rose-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low:    'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const journeyStages = [
  { key: 'discover', label: 'Discover' },
  { key: 'assess',   label: 'Assess' },
  { key: 'learn',    label: 'Learn' },
  { key: 'build',    label: 'Build' },
  { key: 'grow',     label: 'Grow' },
  { key: 'lead',     label: 'Lead' },
];

const skillGroups = [
  { key: 'coreSubjectSkills', label: 'Core subject skills', tone: 'teal'   },
  { key: 'technicalTools',    label: 'Technical tools',     tone: 'blue'   },
  { key: 'humanSkills',       label: 'Human skills',        tone: 'rose'   },
  { key: 'aiSkills',          label: 'AI skills',           tone: 'purple' },
  { key: 'futureSkills',      label: 'Future skills',       tone: 'amber'  },
];

export default function AIPathwayPreview() {
  const { roleId } = useParams();
  const [role, setRole] = useState(null);
  const [deptName, setDeptName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, [roleId]);
  useEffect(() => { document.title = role?.title ? `${role.title} · Future AI Agent Pathway` : 'Future AI Agent Pathway'; }, [role?.title]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(''); setRole(null); setDeptName(null);
    (async () => {
      try {
        // Fetch the single role (gate-protected) and the dept-name lookup
        // (lean — 17 items, all public names already).
        const [roleRes, deptsRes] = await Promise.all([
          axios.get(`${API}/api/ai-pathway/${roleId}`),
          axios.get(`${API}/api/ai-pathway/departments`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const r = roleRes.data || null;
        setRole(r);
        if (r) recordCardEvent('preview_explored', { preview_id: `ai_pathway:${roleId}`, name: r.name || r.title });
        // Resolve department label by matching role.category against the
        // dept label list (so the hero shows the same dept pill as admin).
        if (r && Array.isArray(deptsRes.data)) {
          const m = deptsRes.data.find((d) => d.label === r.category);
          setDeptName(m ? m.label : null);
        }
      } catch (e) {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 404) setErr('This preview is being added.');
        else if (st === 403) setErr('Open this preview from your picks.');
        else if (st === 401) setErr('Your session expired — please reload the page.');
        else { const d = e?.response?.data?.detail; setErr(typeof d === 'string' ? d : (d?.message || 'This preview is outside your committed scope.')); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roleId]);

  const journey = role?.careerJourney || {};
  const skills = role?.skillsRequired || {};
  const fit = role?.careerFit || {};
  const ria = role?.riskAnalysis || {};

  const visibleSections = useMemo(() => {
    if (!role) return [];
    const has = (id) => {
      switch (id) {
        case 'snapshot':   return true;
        case 'agent-reality': return !!role.agentRealityOpening;
        case 'outlook-2035': return !!role.futureOutlook2035;
        case 'market-evidence': return !!role.marketEvidenceReport;
        case 'who':        return Array.isArray(role.whoIsThisFor) && role.whoIsThisFor.length > 0;
        case 'why-exciting':   return !!role.whyThisCareerIsExciting;
        case 'reality':        return !!role.realityCheck?.ratings;
        case 'earning-potential': return Array.isArray(role.earningPotential) && role.earningPotential.length > 0;
        case 'learning-seq':   return Array.isArray(role.practicalLearningSequence) && role.practicalLearningSequence.length > 0;
        case 'role-2030':      return !!role.thisRoleIn2030;
        case 'ai-team':        return !!role.workingWithYourAiTeam;
        case 'realistic-path': return !!role.aRealisticPath;
        case 'similar':        return Array.isArray(role.similarCareers) && role.similarCareers.length > 0;
        case 'employers':      return !!role.exampleEmployers?.examples;
        case 'choose':         return !!(role.chooseThisCareerIf?.length || role.thinkTwiceIf?.length);
        case 'journey':    return Object.keys(journey).length > 0;
        case 'readiness':  return !!role.readinessScore?.factors;
        case 'fit':        return !!(fit.bestFor || fit.interests || fit.learningStyle || fit.matchSignals);
        case 'skills':     return Object.keys(skills).length > 0;
        case 'ai-in-prof': return Array.isArray(role.aiInProfession) && role.aiInProfession.length > 0;
        case 'ai-agents':  return !!role.aiAgentsIntegrated;
        case 'risks':      return Object.keys(ria).length > 0;
        case 'growth':     return Array.isArray(role.growthPotential) && role.growthPotential.length > 0;
        case 'portfolio':  return Array.isArray(role.portfolioIdeas) && role.portfolioIdeas.length > 0;
        case 'earning':    return Array.isArray(role.earningPaths) && role.earningPaths.length > 0;
        case 'mistakes':   return Array.isArray(role.commonMistakes) && role.commonMistakes.length > 0;
        default: return false;
      }
    };
    return SECTIONS.filter((c) => has(c.id));
  }, [role, journey, skills, fit, ria]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]" data-testid="ai-preview-loading">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (err || !role) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md" data-testid="ai-preview-error">
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {err || 'This preview is being added.'}
            </p>
            <Button asChild className="mt-4 bg-teal-600 hover:bg-teal-700">
              <Link to="/ai-pathway">Back to Future AI Agent Pathway</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* ── Hero — mirrors admin AIRoleDetail.jsx ── */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link
            to="/ai-pathway"
            data-testid="ai-preview-back"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold"
          >
            <ArrowLeft size={14} /> Back to Future AI Agent Pathway
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-bold">
            <span className="bg-white/25 backdrop-blur px-3 py-1 rounded-full" data-testid="ai-preview-id">
              #{String(role.id).padStart(3, '0')}
            </span>
            {deptName && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full inline-flex items-center gap-1" data-testid="ai-preview-dept">
                <Building2 size={11} /> {deptName}
              </span>
            )}
            {role.category && role.category !== deptName && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="ai-preview-category">
                {role.category}
              </span>
            )}
            <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full">AI-Agent-Integrated</span>
            {role.titleDisplayBadge && <span className="bg-amber-300 text-amber-950 px-3 py-1 rounded-full" data-testid="ai-preview-concept-badge">{role.titleDisplayBadge}</span>}
          </div>
          <h1
            data-testid="ai-preview-title"
            className="text-3xl md:text-5xl font-black leading-tight mb-6"
          >
            {role.title}
          </h1>
          {role.summary && (
            <blockquote className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-5 py-5 md:px-7 md:py-6" data-testid="ai-preview-summary">
              <Quote size={20} className="text-amber-300 mb-2.5" />
              <p className="text-base md:text-lg text-white leading-relaxed italic">{role.summary}</p>
            </blockquote>
          )}
        </div>
      </section>

      {/* ── Sticky in-page horizontal pill nav (matches admin) ── */}
      <div className="lxt-sticky-nav z-30 bg-[#FFF9F1] border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-2.5 flex gap-2 overflow-x-auto no-scrollbar">
          {visibleSections.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => document.getElementById(c.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              data-testid={`ai-section-nav-${c.id}`}
              className="shrink-0 text-xs font-bold text-gray-600 bg-white border border-gray-200 hover:border-teal-400 hover:text-teal-800 rounded-full px-3.5 py-1.5 transition-colors"
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Snapshot — readiness ring + summary + bestFor (mirrors admin) */}
        <AgentRealitySection role={role} Section={Section} icon={Bot} />

        <Section id="snapshot" icon={Sparkles} title="Snapshot" accent="teal" testId="ai-section-snapshot">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <ReadinessOverall value={role.readinessScore?.overall} />
            <div className="flex-1">
              {(Array.isArray(role.overview) && role.overview.length > 0 ? role.overview : [role.summary]).filter(Boolean).map((t, i) => (
                <p key={i} className="text-[15px] text-gray-800 leading-relaxed mb-3">{t}</p>
              ))}
              {fit.bestFor?.length > 0 && (
                <>
                  <p className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-2">Best for</p>
                  <ChipList items={fit.bestFor} tone="teal" />
                </>
              )}
            </div>
          </div>
          {role.snapshotSummary && (
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              {role.snapshotSummary.dailyLifeImpact && <InfoCard label="Daily life impact" tone="sky">{role.snapshotSummary.dailyLifeImpact}</InfoCard>}
              {role.snapshotSummary.technicalCore && <InfoCard label="Technical core" tone="violet">{role.snapshotSummary.technicalCore}</InfoCard>}
              {role.snapshotSummary.entryProof && <InfoCard label="Entry proof" tone="mint">{role.snapshotSummary.entryProof}</InfoCard>}
            </div>
          )}
        </Section>

        {/* Market Evidence Report */}
        {role.marketEvidenceReport && (
          <Section id="market-evidence" icon={FileSearch} title="Market Evidence Report" accent="blue" testId="ai-section-market-evidence">
            {role.marketEvidenceReport.researchCutoff && (
              <p className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-3">Research cut-off: {role.marketEvidenceReport.researchCutoff}</p>
            )}
            {role.marketEvidenceReport.marketStatus && (
              <p className="text-[15px] text-gray-800 leading-relaxed mb-3">{role.marketEvidenceReport.marketStatus}</p>
            )}
            {role.marketEvidenceReport.evidenceNote && (
              <p className="text-[15px] text-gray-800 leading-relaxed mb-3">{role.marketEvidenceReport.evidenceNote}</p>
            )}
            {role.marketEvidenceReport.sourceLinks?.length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] uppercase font-bold tracking-widest text-gray-600 mb-2">Sources</p>
                <ul className="space-y-1.5">
                  {role.marketEvidenceReport.sourceLinks.map((u, i) => (
                    <li key={i}>
                      <a href={u} target="_blank" rel="noopener noreferrer" className="text-sm text-teal-600 hover:text-teal-700 underline break-all">{u}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {role.marketEvidenceReport.evidenceLimitation && (
              <p className="text-xs text-gray-500 italic leading-relaxed">{role.marketEvidenceReport.evidenceLimitation}</p>
            )}
          </Section>
        )}

        {/* Who Is This For */}
        {role.whoIsThisFor?.length > 0 && (
          <Section id="who" icon={Target} title="Who Is This For?" accent="rose" testId="ai-section-who">
            <Bullets items={role.whoIsThisFor} tone="rose" />
          </Section>
        )}

        {/* Why This Career Is Exciting */}
        {role.whyThisCareerIsExciting && (
          <Section id="why-exciting" icon={Zap} title="Why This Career Is Exciting" accent="amber" testId="ai-section-why-exciting">
            <p className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-line">{role.whyThisCareerIsExciting}</p>
          </Section>
        )}

        {/* Career Journey — 6 numbered steppers */}
        {Object.keys(journey).length > 0 && (
          <Section id="journey" icon={Compass} title="Career Journey" accent="indigo" testId="ai-section-journey">
            <div className="space-y-3">
              {journeyStages.filter((s) => journey[s.key]).map((s, i) => (
                <div key={s.key} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-5">
                    <div className="flex items-center gap-3 md:flex-col md:items-center md:w-24 shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-extrabold text-sm flex items-center justify-center">
                        {i + 1}
                      </div>
                      <p className="font-bold text-gray-900 text-sm md:text-center md:mt-1">{s.label}</p>
                    </div>
                    <p className="flex-1 text-[15px] text-gray-800 leading-relaxed">{journey[s.key]}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Readiness Score */}
        {role.readinessScore?.factors && (
          <Section id="readiness" icon={Activity} title="Readiness Score" accent="purple" testId="ai-section-readiness">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
              {Object.entries(role.readinessScore.factors).map(([k, v]) => (
                <FactorBar key={k} label={k.includes(' ') ? k : humanize(k)} value={v} />
              ))}
            </div>
            {role.readinessScore.notes?.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {role.readinessScore.notes.map((n, i) => (
                  <p key={i} className="text-xs text-gray-500 italic leading-relaxed">{n}</p>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Reality Check */}
        {role.realityCheck?.ratings && (
          <Section id="reality" icon={Star} title="Reality Check" accent="indigo" testId="ai-section-reality">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(role.realityCheck.ratings).map(([k, v]) => (
                <div key={k} className="rounded-2xl border border-gray-200 bg-white p-4">
                  <p className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1.5">{k}</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={16} className={s <= v ? 'text-amber-500 fill-amber-400' : 'text-gray-300'} />
                    ))}
                    <span className="ml-1.5 text-sm font-bold text-gray-900">{typeof v === 'number' ? `${v}/5` : 'Not observed'}</span>
                  </div>
                </div>
              ))}
            </div>
            {role.realityCheck.notes?.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {role.realityCheck.notes.map((n, i) => (
                  <p key={i} className="text-xs text-gray-500 italic leading-relaxed">{n}</p>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Earning Potential */}
        {role.earningPotential?.length > 0 && (
          <Section id="earning-potential" icon={Wallet} title="Earning Potential" accent="emerald" testId="ai-section-earning-potential">
            <Bullets items={role.earningPotential} tone="emerald" />
          </Section>
        )}

        {/* Career Fit — 4 info cards */}
        {(fit.bestFor || fit.interests || fit.learningStyle || fit.matchSignals) && (
          <Section id="fit" icon={CheckCircle2} title="Career Fit" accent="emerald" testId="ai-section-fit">
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              {fit.bestFor?.length > 0 && (
                <InfoCard label="Best for" tone="mint">
                  <ChipList items={fit.bestFor} tone="emerald" />
                </InfoCard>
              )}
              {fit.interests?.length > 0 && (
                <InfoCard label="Interests" tone="sky">
                  <ChipList items={fit.interests} tone="blue" />
                </InfoCard>
              )}
              {fit.learningStyle?.length > 0 && (
                <InfoCard label="Learning style" tone="violet">
                  <ChipList items={role.careerFitDisplayOverrides?.learningStyle || fit.learningStyle} tone="purple" />
                </InfoCard>
              )}
              {fit.matchSignals?.length > 0 && (
                <InfoCard label="Match signals" tone="cream">
                  <Bullets items={fit.matchSignals} tone="amber" />
                </InfoCard>
              )}
            </div>
          </Section>
        )}

        {/* Skills Required */}
        {Object.keys(skills).length > 0 && (
          <Section id="skills" icon={GraduationCap} title="Skills Required" accent="teal" testId="ai-section-skills">
            <div className="space-y-4">
              {skillGroups.filter((g) => skills[g.key]?.length > 0).map((g) => (
                <div key={g.key}>
                  <p className="text-[11px] uppercase font-bold tracking-widest text-gray-600 mb-2">{g.label}</p>
                  <ChipList items={skills[g.key]} tone={g.tone} />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Practical Learning Sequence */}
        {role.practicalLearningSequence?.length > 0 && (
          <Section id="learning-seq" icon={ListChecks} title="Practical Learning Sequence" accent="indigo" testId="ai-section-learning-seq">
            <div className="space-y-3">
              {role.practicalLearningSequence.map((t, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-extrabold text-sm flex items-center justify-center shrink-0">{i + 1}</div>
                  <p className="flex-1 text-[15px] text-gray-800 leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
            <LearningExtras role={role} indiaGuide={role.guides?.indiaPracticalGuide} />
          </Section>
        )}

        {/* AI in this Profession */}
        {role.aiInProfession?.length > 0 && (
          <Section id="ai-in-prof" icon={Sparkles} title="AI in this Profession" accent="cyan" testId="ai-section-aiprof">
            <Bullets items={role.aiInProfession} tone="teal" />
          </Section>
        )}

        {/* AI Agents Integrated */}
        {role.aiAgentsIntegrated && (
          <Section id="ai-agents" icon={Bot} title="AI Agents Integrated" accent="purple" testId="ai-section-agents">
            {role.aiAgentsIntegrated.summary && (
              <InfoCard label="Summary" tone="violet">{role.aiAgentsIntegrated.summary}</InfoCard>
            )}
            {role.aiAgentsIntegrated.howIntegrationHappens && (
              <div className="mt-3">
                <InfoCard label="How integration happens" tone="sky">{role.aiAgentsIntegrated.howIntegrationHappens}</InfoCard>
              </div>
            )}
            {role.aiAgentsIntegrated.agents?.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase font-bold tracking-widest text-gray-600 mb-3">The agents</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {role.aiAgentsIntegrated.agents.map((a, i) => (
                    <div key={i} className="rounded-2xl border border-purple-200 bg-purple-50/50 p-4 md:p-5" data-testid={`ai-agent-${i}`}>
                      <div className="flex items-start gap-3 mb-2">
                        <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-purple-400 to-fuchsia-500 text-white flex items-center justify-center shrink-0 shadow-sm">
                          <Bot size={16} />
                        </span>
                        <p className="font-bold text-gray-900 leading-snug">{a.agent}</p>
                      </div>
                      {a.integration && (
                        <p className="text-sm text-gray-700 leading-relaxed mb-3">{a.integration}</p>
                      )}
                      {a.exampleTools?.length > 0 && (
                        <>
                          <p className="text-[10px] uppercase font-bold tracking-widest text-purple-700 mb-1.5">Example tools</p>
                          <div className="flex flex-wrap gap-1.5">
                            {a.exampleTools.map((t, j) => (
                              <span key={j} className="text-[12px] px-2 py-0.5 rounded-full bg-white border border-purple-200 text-purple-800 font-semibold">{t}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* This Role in 2030 */}
        {role.thisRoleIn2030 && (
          <Section id="role-2030" icon={Rocket} title="This Role in 2030" accent="purple" testId="ai-section-role-2030">
            <p className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-line">{role.thisRoleIn2030}</p>
          </Section>
        )}

        <OutlookSection role={role} Section={Section} icon={Rocket} />

        {/* Working With Your AI Team */}
        {role.workingWithYourAiTeam && (
          <Section id="ai-team" icon={Users} title="Working With Your AI Team" accent="cyan" testId="ai-section-ai-team">
            <div className="grid md:grid-cols-2 gap-3">
              {role.workingWithYourAiTeam.delegateToAgents?.length > 0 && (
                <InfoCard label="Delegate to agents" tone="mint">
                  <Bullets items={role.workingWithYourAiTeam.delegateToAgents} tone="emerald" />
                </InfoCard>
              )}
              {role.workingWithYourAiTeam.neverDelegate?.length > 0 && (
                <InfoCard label="Never delegate" tone="rose">
                  <Bullets items={role.workingWithYourAiTeam.neverDelegate} tone="rose" />
                </InfoCard>
              )}
            </div>
            {role.workingWithYourAiTeam.earningTrust && (
              <div className="mt-3">
                <InfoCard label="Earning trust" tone="violet">{role.workingWithYourAiTeam.earningTrust}</InfoCard>
              </div>
            )}
          </Section>
        )}

        {/* A Realistic Path */}
        {role.aRealisticPath && (
          <Section id="realistic-path" icon={Footprints} title="A Realistic Path" accent="teal" testId="ai-section-realistic-path">
            <p className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-line">{role.aRealisticPath}</p>
            <p className="mt-3 text-xs text-gray-500 italic">Fictional illustrative composite, not a testimonial.</p>
          </Section>
        )}

        {/* Risk Analysis */}
        {Object.keys(ria).length > 0 && (
          <Section id="risks" icon={ShieldAlert} title="Risk Analysis" accent="rose" testId="ai-section-risk">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(ria).map(([k, v]) => (
                <div key={k} className="rounded-2xl border border-gray-200 bg-white p-4">
                  <p className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-1.5">{humanize(k)}</p>
                  <span className={`inline-block text-sm px-3 py-1 rounded-full border font-semibold ${RISK_TONES[v] || 'bg-slate-100 text-slate-800 border-slate-200'}`}>
                    {v}
                  </span>
                </div>
              ))}
            </div>
            {role.riskNotes?.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {role.riskNotes.map((n, i) => (
                  <p key={i} className="text-xs text-gray-500 italic leading-relaxed">{n}</p>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Growth Potential */}
        {role.growthPotential?.length > 0 && (
          <Section id="growth" icon={TrendingUp} title="Growth Potential" accent="emerald" testId="ai-section-growth">
            <Bullets items={role.growthPotential} tone="emerald" />
          </Section>
        )}

        {/* Similar Careers */}
        {role.similarCareers?.length > 0 && (
          <Section id="similar" icon={Layers} title="Similar Careers" accent="blue" testId="ai-section-similar">
            <Bullets items={role.similarCareers} tone="blue" />
          </Section>
        )}

        {/* Example Employers */}
        {role.exampleEmployers?.examples && (
          <Section id="employers" icon={Building2} title="Example Employers" accent="teal" testId="ai-section-employers">
            <p className="text-[15px] text-gray-800 leading-relaxed">{role.exampleEmployers.examples}</p>
            {role.exampleEmployers.note && (
              <p className="mt-3 text-xs text-gray-500 italic leading-relaxed">{role.exampleEmployers.note}</p>
            )}
          </Section>
        )}

        {/* Portfolio Ideas — numbered 01/02 cards */}
        {role.portfolioIdeas?.length > 0 && (
          <Section id="portfolio" icon={Lightbulb} title="Portfolio Ideas" accent="amber" testId="ai-section-portfolio">
            <div className="grid sm:grid-cols-2 gap-3">
              {role.portfolioIdeas.map((p, i) => (
                <div key={i} className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-amber-700 font-extrabold text-base shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    {typeof p === 'string' ? (
                      <p className="text-[15px] text-gray-800 leading-relaxed">{p}</p>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-bold text-gray-900 leading-snug">{p.title}</p>
                        {p.badgeProject && (
                          <span className="inline-block mt-1.5 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-200/70 text-amber-900">Badge project</span>
                        )}
                        {p.details && (
                          <div className="mt-2 space-y-1.5">
                            {Object.entries(p.details).map(([dk, dv]) => (
                              <p key={dk} className="text-sm text-gray-800 leading-relaxed">
                                <span className="font-semibold text-gray-900">{humanize(dk)}: </span>{dv}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Earning Paths */}
        {role.earningPaths?.length > 0 && (
          <Section id="earning" icon={DollarSign} title="Earning Paths" accent="emerald" testId="ai-section-earning">
            <Bullets items={role.earningPaths} tone="emerald" />
          </Section>
        )}

        {/* Choose This Career If / Think Twice If */}
        {(role.chooseThisCareerIf?.length > 0 || role.thinkTwiceIf?.length > 0) && (
          <Section id="choose" icon={Scale} title="Choose This Career If / Think Twice If" accent="emerald" testId="ai-section-choose">
            <div className="grid md:grid-cols-2 gap-3">
              {role.chooseThisCareerIf?.length > 0 && (
                <InfoCard label="Choose this career if" tone="mint">
                  <Bullets items={role.chooseThisCareerIf} tone="emerald" />
                </InfoCard>
              )}
              {role.thinkTwiceIf?.length > 0 && (
                <InfoCard label="Think twice if" tone="rose">
                  <Bullets items={role.thinkTwiceIf} tone="rose" />
                </InfoCard>
              )}
            </div>
          </Section>
        )}

        {/* Common Mistakes — AlertCircle bullets */}
        {role.commonMistakes?.length > 0 && (
          <Section id="mistakes" icon={AlertCircle} title="Common Mistakes" accent="rose" testId="ai-section-mistakes">
            <ul className="space-y-2.5">
              {role.commonMistakes.map((m, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                  <AlertCircle size={18} className="text-rose-600 mt-0.5 shrink-0" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <EarningRoutes data={role.guides?.optionalEarningRoutes} />
        <LiveOneDayButton pathKey="ai_pathway" itemId={roleId} roleName={role.title} />
      </main>

      <Footer />
    </div>
  );
}
